import React, {
    useRef,
    useState,
    useEffect,
    useLayoutEffect,
    useMemo,
    useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import { getUser } from "../../auth/Auth";
import { postRequest, deleteRequest, getRequest } from "../../routes/Routes";
import {
    loadWish,
    loadTemplates,
    loadTemplateTexts,
    loadWishTexts, // <-- added
} from "../../common/Common";

/* ───────────────────────────── Utils ───────────────────────────── */
const uid = () => Math.random().toString(36).slice(2, 9);
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const DEFAULT_BG_GRAD = "from-pink-100 via-purple-50 to-blue-100";
const DEFAULT_TEXT = "#1f2937";

const resolveText = (raw, vars) =>
    String(raw ?? "")
        .replaceAll("{receiverName}", vars.receiverName ?? "")
        .replaceAll("{senderName}", vars.senderName ?? "")
        .replaceAll("{message}", vars.message ?? "");

const textStyleToInline = (style) => {
    const s = style || {};
    return {
        color: s.gradient ? undefined : s.color || DEFAULT_TEXT,
        fontSize: (s.fontSize ?? 24) + "px",
        fontWeight: s.fontWeight ?? 600,
        lineHeight: s.lineHeight ?? 1.2,
        letterSpacing:
            typeof s.letterSpacing === "number" ? s.letterSpacing + "px" : undefined,
        fontStyle: s.italic ? "italic" : "normal",
        textTransform: s.uppercase ? "uppercase" : "none",
        textShadow: s.shadow ? "0 2px 12px rgba(0,0,0,.20)" : undefined,
    };
};
const alignToClass = (align) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

const num = (v, fallback = 0) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : fallback;
};

const extractMediaUrl = (bg) => {
    if (!bg) return "";
    if (Array.isArray(bg)) return bg[bg.length - 1] || "";
    if (typeof bg === "string") {
        const trimmed = bg.trim();
        if (trimmed.startsWith("[")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed) && parsed.length) return parsed[parsed.length - 1] || "";
            } catch { }
        }
        return trimmed.replace(/^"(.*)"$/, "$1");
    }
    return "";
};

/* ───────────── API mappers (template & wish/wish-template text rows) ───────────── */
const fromApiTemplate = (t) => ({
    id: String(t.id ?? ""),
    type: t.type || "birthday",
    name: t.name || "",
    description: t.description ?? "",
    backgroundGif: t.background_gif ?? t.backgroundGif ?? "",
    backgroundColor: t.background_color ?? t.backgroundColor ?? DEFAULT_BG_GRAD,
    textColor: t.text_color ?? t.textColor ?? DEFAULT_TEXT,
    accentColor: t.accent_color ?? t.accentColor ?? "",
    bgOpacity:
        typeof t.background_opacity === "number"
            ? t.background_opacity
            : typeof t.bg_opacity === "number"
                ? t.bg_opacity
                : t.bgOpacity ?? 0.28,
    bgBlurAmount:
        typeof t.background_blur === "number"
            ? t.background_blur
            : typeof t.bg_blur_amount === "number"
                ? t.bg_blur_amount
                : t.bgBlurAmount ?? 0,
    createdAt: t.created_at || new Date().toISOString(),
});

const groupByType = (list) =>
    list.reduce((acc, raw) => {
        const t = fromApiTemplate(raw);
        (acc[t.type] ||= []).push(t);
        return acc;
    }, {});

const mapRowToBlock = (r) => ({
    id: String(r.id ?? uid()),
    type: r.block_type || "custom",
    // prefer placeholder if present; else use resolved text from wish-text
    text: r.default_text ?? r.text ?? "",
    x: num(r.position_x ?? r.x ?? 10, 10),
    y: num(r.position_y ?? r.y ?? 10, 10),
    w: num(r.width ?? 80, 80),
    rotate: num(r.rotate ?? 0, 0),
    style: {
        color: r.color ?? DEFAULT_TEXT,
        gradient: r.gradient || undefined,
        fontSize: num(r.font_size ?? 24, 24),
        fontWeight: num(r.weight ?? 600, 600),
        lineHeight: num(r.line_height ?? r.lineHeight ?? 1.2, 1.2),
        textAlign: r.align || "left",
        italic: !!r.italic,
        uppercase: !!r.uppercase,
        shadow: !!r.shadow,
    },
});

/* payload used to persist template text rows (wish-template-text) */
const toTextPayload = (templateId, b) => ({
    wish_template_id: templateId,
    block_type: b.type,
    default_text: b.text,
    color: b.style?.color ?? DEFAULT_TEXT,
    gradient: b.style?.gradient ?? null,
    font_size: b.style?.fontSize ?? 24,
    weight: b.style?.fontWeight ?? 600,
    rotate: b.rotate ?? 0,
    align: b.style?.textAlign ?? "left",
    width: b.w ?? 100,
    italic: !!b.style?.italic,
    uppercase: !!b.style?.uppercase,
    shadow: !!b.style?.shadow,
    position_x: typeof b.x === "number" ? Math.round(b.x * 100) / 100 : 0,
    position_y: typeof b.y === "number" ? Math.round(b.y * 100) / 100 : 0,
});

/* payload used to create wish-specific rows (wish-text) */
const toWishTextPayload = (wishId, templateId, b, vars) => ({
    wish_id: wishId,
    wish_template_id: templateId,
    block_type: b.type,
    default_text: b.text,
    text: resolveText(b.text, vars),
    color: b.style?.color ?? DEFAULT_TEXT,
    gradient: b.style?.gradient ?? null,
    font_size: b.style?.fontSize ?? 24,
    weight: b.style?.fontWeight ?? 600,
    rotate: b.rotate ?? 0,
    align: b.style?.textAlign ?? "left",
    width: b.w ?? 100,
    italic: !!b.style?.italic,
    uppercase: !!b.style?.uppercase,
    shadow: !!b.style?.shadow,
    position_x: typeof b.x === "number" ? Math.round(b.x * 100) / 100 : 0,
    position_y: typeof b.y === "number" ? Math.round(b.y * 100) / 100 : 0,
});

/* create/update wish-text rows for current blocks */
const saveWishTexts = async (token, wishId, templateId, layout, vars, isEditing = false) => {
    if (!Array.isArray(layout) || !layout.length) {
        // If editing and layout is empty, delete all existing wish-text rows
        if (isEditing) {
            try {
                const response = await getRequest(`/api/wish-text?wish_id=${wishId}`, token);
                if (response.status === 200 && response.data?.wishTexts) {
                    const existingIds = response.data.wishTexts.map((wt) => String(wt.id));
                    await Promise.allSettled(
                        existingIds.map((id) => deleteRequest(`/api/wish-text/${id}`, token))
                    );
                }
            } catch {
                // Ignore errors when cleaning up
            }
        }
        return { ok: true, failures: 0 };
    }

    let existingWishTexts = [];
    if (isEditing) {
        // Fetch existing wish-text rows for this wish
        try {
            const response = await getRequest(`/api/wish-text?wish_id=${wishId}`, token);
            if (response.status === 200 && response.data?.wishTexts) {
                existingWishTexts = response.data.wishTexts;
            }
        } catch {
            // If fetch fails, continue with create-only approach
        }
    }

    // Separate blocks into existing (with numeric IDs) and new (without numeric IDs)
    const existingBlocks = layout.filter((b) => /^\d+$/.test(String(b.id)));
    const newBlocks = layout.filter((b) => !/^\d+$/.test(String(b.id)));

    // Find existing wish-text IDs that are no longer in the layout (to delete)
    const layoutIds = new Set(existingBlocks.map((b) => String(b.id)));
    const toDelete = existingWishTexts.filter((wt) => !layoutIds.has(String(wt.id)));

    let failures = 0;

    // Delete wish-text rows that are no longer in the layout
    const deleteResults = await Promise.allSettled(
        toDelete.map((wt) => deleteRequest(`/api/wish-text/${wt.id}`, token))
    );
    failures += deleteResults.filter(
        (r) => r.status === "rejected" || (r.value && (r.value.status !== 200 || r.value?.data?.error))
    ).length;

    // Update existing wish-text rows
    const updateResults = await Promise.allSettled(
        existingBlocks.map((b) =>
            postRequest({
                endpoint: `/api/wish-text/update/${b.id}`,
                token,
                body: toWishTextPayload(wishId, templateId, b, vars),
            })
        )
    );
    failures += updateResults.filter(
        (r) => r.status === "rejected" || (r.value && (r.value.status !== 200 || r.value?.data?.error))
    ).length;

    // Create new wish-text rows
    const createResults = await Promise.allSettled(
        newBlocks.map((b) =>
            postRequest({
                endpoint: "/api/wish-text",
                token,
                body: toWishTextPayload(wishId, templateId, b, vars),
            })
        )
    );
    failures += createResults.filter(
        (r) => r.status === "rejected" || (r.value && (r.value.status !== 200 || r.value?.data?.error))
    ).length;

    return { ok: failures === 0, failures };
};

/* ─────────────────────── Scaled preview shell ─────────────────────── */
function ScaledPreview({ baseWidth = 360, baseHeight = 720, children }) {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [containerHeight, setContainerHeight] = useState(baseHeight + 32);
    const padding = 16;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const resize = () => {
            const { width } = el.getBoundingClientRect();
            if (!width) return;
            const available = width - padding * 2;
            const rawScale = available / baseWidth;
            const nextScale = Math.min(1, rawScale > 0 ? rawScale : 1);
            setScale(nextScale);
            setContainerHeight(baseHeight * nextScale + padding * 2);
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(el);
        return () => ro.disconnect();
    }, [baseWidth, baseHeight]);

    return (
        <div
            ref={containerRef}
            className="border border-2 border-light rounded-3 bg-white d-flex align-items-center justify-content-center"
            style={{ height: containerHeight, overflow: "hidden" }}
        >
            <div
                style={{
                    width: baseWidth,
                    height: baseHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: "center center",
                    borderRadius: "1rem",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    background: "#fff",
                    position: "relative",
                }}
            >
                {children}
            </div>
        </div>
    );
}

/* ───────────────────── Editable Canvas (preview) ───────────────────── */
function EditableCanvas({
    tpl,
    setTpl,
    selectedId,
    setSelectedId,
    previewVars,
}) {
    const canvasRef = useRef(null);
    const [dragState, setDragState] = useState(null);
    const [resizeState, setResizeState] = useState(null);

    const onMouseDownBlock = (e, b) => {
        if (!canvasRef.current) return;
        const path = e.composedPath?.() || [];
        if (path.some((el) => el?.dataset?.editable === "true")) return; // don't start drag if typing
        e.stopPropagation();
        const rect = canvasRef.current.getBoundingClientRect();
        setDragState({
            id: b.id,
            startX: e.clientX,
            startY: e.clientY,
            origX: b.x,
            origY: b.y,
            rectW: rect.width,
            rectH: rect.height,
        });
    };

    const updateBlock = useCallback((id, patch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((blk) => (blk.id === id ? { ...blk, ...patch } : blk)),
        }));
    }, [setTpl]);

    const updateBlockStyle = useCallback((id, patch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((blk) =>
                blk.id === id ? { ...blk, style: { ...(blk.style || {}), ...patch } } : blk
            ),
        }));
    }, [setTpl]);

    const onMouseMove = useCallback(
        (e) => {
            if (dragState) {
                const { startX, startY, origX, origY, rectW, rectH, id } = dragState;
                const dxPct = ((e.clientX - startX) / rectW) * 100;
                const dyPct = ((e.clientY - startY) / rectH) * 100;
                updateBlock(id, {
                    x: clamp(origX + dxPct, 0, 100),
                    y: clamp(origY + dyPct, 0, 100),
                });
            }
            if (resizeState) {
                const { startX, origW, rectW, id } = resizeState;
                const dxPct = ((e.clientX - startX) / rectW) * 100;
                updateBlock(id, { w: clamp(origW + dxPct, 5, 100) });
            }
        },
        [dragState, resizeState, updateBlock]
    );

    const onMouseUp = useCallback(() => {
        setDragState(null);
        setResizeState(null);
    }, []);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    const startResize = (e, b) => {
        if (!canvasRef.current) return;
        e.stopPropagation();
        const rect = canvasRef.current.getBoundingClientRect();
        setResizeState({
            id: b.id,
            startX: e.clientX,
            origW: b.w,
            rectW: rect.width,
        });
    };

    const gradient = tpl.backgroundColor
        ? `bg-gradient-to-br ${tpl.backgroundColor}`
        : `bg-gradient-to-br ${DEFAULT_BG_GRAD}`;
    const bgUrl = extractMediaUrl(tpl.backgroundGif);
    const opacity = typeof tpl.bgOpacity === "number" ? tpl.bgOpacity : 0.28;
    const blur = typeof tpl.bgBlurAmount === "number" ? tpl.bgBlurAmount : 0;

    return (
        <div
            className={`position-relative ${gradient}`}
            style={{ width: "100%", height: "100%" }}
            ref={canvasRef}
            onMouseDown={() => setSelectedId(null)}
        >
            {(tpl.bgLocalPreview || bgUrl) && (
                <img
                    src={tpl.bgLocalPreview || bgUrl}
                    alt="Background"
                    className="position-absolute top-0 start-0 w-100 h-100"
                    style={{ objectFit: "cover", opacity, filter: blur ? `blur(${blur}px)` : undefined }}
                />
            )}

            {tpl.layout.map((b) => {
                const isSel = b.id === selectedId;
                const left = `${b.x}%`;
                const top = `${b.y}%`;
                const width = `${b.w}%`;
                const alignCls = alignToClass(b.style?.textAlign);
                const styleInline = textStyleToInline({
                    ...b.style,
                    color: b.style?.color || tpl.textColor || DEFAULT_TEXT,
                });
                const text = resolveText(b.text, { ...previewVars });

                return (
                    <div
                        key={b.id}
                        className={`position-absolute ${alignCls}`}
                        style={{ left, top, width, transform: `rotate(${b.rotate || 0}deg)` }}
                        onMouseDown={(e) => onMouseDownBlock(e, b)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedId(b.id);
                        }}
                    >
                        <span
                            data-editable="true"
                            contentEditable
                            suppressContentEditableWarning
                            className={`${b.style?.gradient ? `bg-gradient-to-r ${b.style.gradient} bg-clip-text text-transparent` : ""
                                } ${isSel ? "border border-primary rounded px-1" : ""} d-inline-block`}
                            style={{ ...styleInline, cursor: "text" }}
                            onBlur={(e) => updateBlock(b.id, { text: e.currentTarget.innerText })}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") e.preventDefault();
                            }}
                        >
                            {text}
                        </span>

                        {/* resize knob */}
                        <div
                            onMouseDown={(e) => startResize(e, b)}
                            className="position-absolute"
                            style={{
                                right: -8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                height: 16,
                                width: 16,
                                borderRadius: 999,
                                background: isSel ? "#6366f1" : "#d1d5db",
                                boxShadow: "0 1px 2px rgba(0,0,0,.15)",
                                cursor: "ew-resize",
                            }}
                            title="Drag to resize width"
                        />
                    </div>
                );
            })}
        </div>
    );
}

/* ───────────────────────────── Main Form ───────────────────────────── */
function QRForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templatesByType, setTemplatesByType] = useState({});

    const [formData, setFormData] = useState({
        id: "",
        type: "birthday",
        receiverName: "",
        senderName: "",
        templateId: "",
    });

    // preview template state
    const [tpl, setTpl] = useState({
        id: "",
        type: "birthday",
        name: "",
        description: "",
        backgroundGif: "",
        backgroundColor: DEFAULT_BG_GRAD,
        textColor: DEFAULT_TEXT,
        accentColor: "",
        bgOpacity: 0.28,
        bgBlurAmount: 0,
        layout: [],
    });

    // selection + deletions (for server sync of template-text)
    const [selectedId, setSelectedId] = useState(null);
    const [deletedIds, setDeletedIds] = useState([]);

    const wishTypes = [
        { value: "birthday", label: "🎂 Birthday", description: "Celebrate another year of life" },
        { value: "anniversary", label: "❤️ Anniversary", description: "Commemorate special milestones" },
        { value: "congratulations", label: "🎉 Congratulations", description: "Celebrate achievements" },
        { value: "retirement", label: "🏆 Retirement", description: "Honor years of service" },
        { value: "graduation", label: "🎓 Graduation", description: "Academic accomplishments" },
        { value: "wedding", label: "💒 Wedding", description: "Celebrate new beginnings" },
        { value: "baby", label: "👶 Baby Shower", description: "Welcome the little one" },
        { value: "love", label: "💕 Love Note", description: "Express your feelings" },
    ];

    const fromApiWish = (w) => ({
        id: String(w.id ?? ""),
        type: w.type ?? "birthday",
        receiverName: w.receiver_name ?? "",
        senderName: w.sender_name ?? "",
        templateId: w.page_template ? String(w.page_template) : "",
    });

    /* load user, templates, and wish (if editing) */
    useEffect(() => {
        getUser()
            .then(async (u) => {
                setUser(u);

                setLoadingTemplates(true);
                try {
                    const res = await loadTemplates(u?.token);
                    const items = Array.isArray(res)
                        ? res
                        : res?.wishtemplates || res?.wishTemplates || [];
                    setTemplatesByType(groupByType(items));
                } finally {
                    setLoadingTemplates(false);
                }

                if (isEditing && /^\d+$/.test(id)) {
                    setLoadingContent(true);
                    try {
                        const apiWish = await loadWish(id, u?.token);
                        if (apiWish) setFormData(fromApiWish(apiWish));
                    } finally {
                        setLoadingContent(false);
                    }
                }
            })
            .catch(() => setUser(null));
    }, [id, isEditing]);

    // ensure selection for type
    useEffect(() => {
        const list = templatesByType[formData.type] || [];
        if (!list.length) return;
        const exists = list.some((t) => String(t.id) === String(formData.templateId));
        if (!formData.templateId || !exists) {
            setFormData((prev) => ({ ...prev, templateId: String(list[0].id) }));
        }
    }, [formData.type, templatesByType]); // eslint-disable-line

    const selectedTypeTemplates = templatesByType[formData.type] || [];
    const selectedTemplate = selectedTypeTemplates.find(
        (t) => String(t.id) === String(formData.templateId)
    );

    // load rows for canvas:
    // - EDITING: load wish-text for this wish
    // - CREATING: load template-text for the selected template
    useEffect(() => {
        (async () => {
            if (!selectedTemplate?.id) {
                setTpl((p) => ({ ...p, id: "", layout: [] }));
                return;
            }
            const meta = fromApiTemplate(selectedTemplate);

            let rows = [];
            try {
                if (isEditing && /^\d+$/.test(id)) {
                    // wish-text snapshot for this wish
                    const data = await loadWishTexts(user?.token);
                    const list = Array.isArray(data) ? data : data?.wishTexts || [];
                    rows = list.filter((r) => String(r.wish_id) === String(id));
                } else {
                    // template-text defaults
                    const data = await loadTemplateTexts(user?.token);
                    const list = Array.isArray(data) ? data : data?.wishTemplateTexts || [];
                    rows = list.filter(
                        (r) =>
                            String(r.wish_template_id ?? r.wishTemplate?.id) === String(selectedTemplate.id)
                    );
                }
            } catch {
                // Fallback to a filtered fetch
                try {
                    const headers = { Accept: "application/json" };
                    if (user?.token) {
                        headers.Authorization = `Bearer ${user.token}`;
                        headers.token = user.token;
                    }
                    const url =
                        isEditing && /^\d+$/.test(id)
                            ? `/api/wish-text?wish_id=${id}`
                            : `/api/wish-template-text?wish_template_id=${selectedTemplate.id}`;
                    const res = await fetch(url, { headers });
                    if (res.ok) {
                        const json = await res.json();
                        rows = isEditing ? json?.wishTexts || [] : json?.wishTemplateTexts || [];
                    }
                } catch {
                    rows = [];
                }
            }

            setTpl({
                ...meta,
                layout: rows.length ? rows.map(mapRowToBlock) : [],
            });
            setSelectedId(null);
            setDeletedIds([]);
        })();
    }, [selectedTemplate?.id, user?.token, isEditing, id]);

    /* helpers to update selected block (Inspector) */
    const selectedBlock = useMemo(
        () => tpl.layout.find((b) => b.id === selectedId) || null,
        [tpl.layout, selectedId]
    );

    const updateBlock = (id, patch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((b) => (b.id === id ? { ...b, ...patch } : b)),
        }));
    };
    const updateBlockStyle = (id, stylePatch) => {
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.map((b) =>
                b.id === id ? { ...b, style: { ...(b.style || {}), ...stylePatch } } : b
            ),
        }));
    };

    const addBlock = (kind) => {
        const basics = {
            id: uid(),
            type: kind,
            text:
                kind === "receiver"
                    ? "Dear {receiverName}"
                    : kind === "sender"
                        ? "{senderName} 💝"
                        : kind === "message"
                            ? '"{message}"'
                            : kind === "heading"
                                ? "Happy"
                                : kind === "subheading"
                                    ? "Birthday!"
                                    : "Add your text",
            x: 10,
            y: 10 + Math.random() * 60,
            w: 60,
            rotate: 0,
            style: {
                color: tpl.textColor || DEFAULT_TEXT,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.2,
                textAlign: "left",
                italic: false,
                uppercase: false,
                shadow: false,
            },
        };
        setTpl((prev) => ({ ...prev, layout: [...prev.layout, basics] }));
        setSelectedId(basics.id);
    };

    const removeSelected = () => {
        if (!selectedBlock) return;
        if (/^\d+$/.test(selectedBlock.id)) {
            setDeletedIds((prev) => [...prev, selectedBlock.id]);
        }
        setTpl((prev) => ({
            ...prev,
            layout: prev.layout.filter((b) => b.id !== selectedBlock.id),
        }));
        setSelectedId(null);
    };

    // persist blocks back to TEMPLATE (wish-template-text)
    const persistTextBlocks = async () => {
        if (!user?.token || !tpl.id) {
            toast.error("Missing auth or template ID.", { position: "bottom-center" });
            return false;
        }

        const removedIds = deletedIds.filter((idStr) => /^\d+$/.test(idStr));
        const existing = tpl.layout.filter((b) => /^\d+$/.test(b.id));
        const created = tpl.layout.filter((b) => !/^\d+$/.test(b.id));

        let failures = 0;

        // delete
        await Promise.all(
            removedIds.map(async (rid) => {
                try {
                    const { status, data } = await deleteRequest(`/api/wish-template-text/${rid}`, user.token);
                    if (!(status === 200 && !data?.error)) failures++;
                } catch {
                    failures++;
                }
            })
        );
        // update
        await Promise.all(
            existing.map(async (b) => {
                try {
                    const { status, data } = await postRequest({
                        endpoint: `/api/wish-template-text/update/${b.id}`,
                        token: user.token,
                        body: toTextPayload(tpl.id, b),
                    });
                    if (!(status === 200 && !data?.error)) failures++;
                } catch {
                    failures++;
                }
            })
        );
        // create
        await Promise.all(
            created.map(async (b) => {
                try {
                    const { status, data } = await postRequest({
                        endpoint: `/api/wish-template-text`,
                        token: user.token,
                        body: toTextPayload(tpl.id, b),
                    });
                    if (!(status === 200 && !data?.error)) failures++;
                } catch {
                    failures++;
                }
            })
        );

        if (failures === 0) {
            toast.success("Text blocks saved.", { position: "bottom-center", autoClose: 2200 });
            return true;
        } else {
            toast.warn(`Saved with ${failures} error(s).`, { position: "bottom-center", autoClose: 3500 });
            return false;
        }
    };

    // submit wish + create wish-text snapshot
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user?.token) {
            navigate("/login");
            return;
        }
        if (!formData.templateId) {
            toast.error("No template available for the selected type.", { position: "bottom-center" });
            return;
        }

        setIsLoading(true);

        try {
            // 1) create/update wish
            const wishPayload = {
                type: formData.type,
                receiver_name: formData.receiverName,
                sender_name: formData.senderName,
                message: "", // message removed from the form
                page_template: formData.templateId,
                template_id: formData.templateId,
                token: user.token,
                endpoint: isEditing ? `/api/wish/update/${id}` : "/api/wish",
            };

            const res = await postRequest(wishPayload);

            if (!(res?.status === 200) || res?.data?.error) {
                toast.error(res?.data?.message || "Failed to save wish", { position: "bottom-center" });
                setIsLoading(false);
                return;
            }

            const wishId = res?.data?.wish?.id || (isEditing ? id : null);
            if (!wishId) {
                toast.error("Wish saved but no ID returned.", { position: "bottom-center" });
                setIsLoading(false);
                return;
            }

            // 2) create wish-text rows (snapshot of current layout)
            const vars = {
                receiverName: formData.receiverName,
                senderName: formData.senderName,
                message: "",
            };

            const saveSnap = await saveWishTexts(
                user.token,
                String(wishId),
                formData.templateId,
                tpl.layout || [],
                vars,
                isEditing
            );
            if (!saveSnap.ok) {
                toast.warn(`Wish saved but ${saveSnap.failures} text block(s) failed to save.`, {
                    position: "bottom-center",
                    autoClose: 3500,
                });
            } else {
                toast.success(isEditing ? "Wish updated" : "Wish created", {
                    position: "bottom-center",
                    autoClose: 2200,
                });
            }

            navigate("/portal/qrs");
        } catch {
            toast.error("Network or server error", { position: "bottom-center" });
        } finally {
            setIsLoading(false);
        }
    };

    const noTemplateForType = !loadingTemplates && selectedTypeTemplates.length === 0;

    return (
        <div className="p-4">
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex align-items-center gap-3 mb-4">
                    <button
                        onClick={() => navigate("/portal/qrs")}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-0">
                            {isEditing ? "Edit QR Code" : "Create New QR Code"}
                        </h1>
                        <p className="text-muted mb-0">
                            {isEditing ? "Update your wish QR code details" : "Create a custom wish QR code that expires in 24 hours"}
                        </p>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Left: meta form */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="col-lg-5">
                        <div className="card rounded-3 shadow-sm h-100">
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label fw-medium text-dark">Wish Type</label>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value, templateId: "" }))}
                                            className="form-select form-select-lg rounded-3"
                                            required
                                        >
                                            {wishTypes.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="form-text text-muted">
                                            {wishTypes.find((t) => t.value === formData.type)?.description}
                                        </div>
                                    </div>

                                    {/* Template list */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium text-dark d-flex align-items-center gap-2">
                                            Choose Template
                                            {loadingTemplates && (
                                                <span className="badge bg-light text-dark d-inline-flex align-items-center gap-2">
                                                    <span className="spinner-border spinner-border-sm" role="status" />
                                                    Loading…
                                                </span>
                                            )}
                                        </label>

                                        {!loadingTemplates && selectedTypeTemplates.length > 0 && (
                                            <div className="row g-3">
                                                {selectedTypeTemplates.map((opt) => {
                                                    const checked = String(formData.templateId) === String(opt.id);
                                                    return (
                                                        <div className="col-12" key={opt.id}>
                                                            <motion.label
                                                                whileHover={{ scale: 1.01 }}
                                                                className={`w-100 card rounded-3 px-3 py-2 cursor-pointer ${checked ? "border-primary bg-primary bg-opacity-10" : "border-light"
                                                                    }`}
                                                            >
                                                                <div className="d-flex flex-wrap align-items-start gap-3">
                                                                    <input
                                                                        type="radio"
                                                                        className="form-check-input mt-1 flex-shrink-0 position-static ms-0"
                                                                        style={{ marginLeft: 0 }}
                                                                        name="templateId"
                                                                        value={opt.id}
                                                                        checked={checked}
                                                                        onChange={() => setFormData((p) => ({ ...p, templateId: String(opt.id) }))}
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                            <h6 className="fw-semibold text-dark mb-1">
                                                                                {opt.name || `Template #${opt.id}`}
                                                                            </h6>
                                                                            <span className="badge bg-light text-dark">{opt.type}</span>
                                                                        </div>
                                                                        <p className="small text-muted mb-2">{opt.description || "—"}</p>
                                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                                            <span className="badge rounded-pill bg-secondary-subtle text-secondary text-wrap" style={{ wordBreak: "break-word" }}>
                                                                                {opt.backgroundColor}
                                                                            </span>
                                                                            {opt.accentColor ? (
                                                                                <span className="badge rounded-pill bg-secondary-subtle text-secondary text-wrap" style={{ wordBreak: "break-word" }}>
                                                                                    {opt.accentColor}
                                                                                </span>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </motion.label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {!loadingTemplates && selectedTypeTemplates.length === 0 && (
                                            <div className="alert alert-warning mb-0">
                                                No templates found for{" "}
                                                <strong>{wishTypes.find((t) => t.value === formData.type)?.label}</strong>. Create one in the Template Manager, then come back.
                                            </div>
                                        )}
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium text-dark">Receiver Name</label>
                                            <input
                                                type="text"
                                                name="receiverName"
                                                value={formData.receiverName}
                                                onChange={(e) => setFormData((p) => ({ ...p, receiverName: e.target.value }))}
                                                placeholder="Who is this for?"
                                                className="form-control form-control-lg rounded-3"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium text-dark">Sender Name</label>
                                            <input
                                                type="text"
                                                name="senderName"
                                                value={formData.senderName}
                                                onChange={(e) => setFormData((p) => ({ ...p, senderName: e.target.value }))}
                                                placeholder="Who is this from?"
                                                className="form-control form-control-lg rounded-3"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex gap-3">
                                        <button
                                            type="button"
                                            onClick={persistTextBlocks}
                                            className="btn btn-outline-secondary flex-fill py-3 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                                            disabled={!tpl.id}
                                        >
                                            Save Text Blocks
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={isLoading || noTemplateForType}
                                            className="btn gradient-secondary text-white flex-fill py-3 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow"
                                            style={{ opacity: isLoading ? 0.7 : 1 }}
                                        >
                                            {isLoading ? (
                                                <div className="spinner-border spinner-border-sm text-white" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <Save size={20} />
                                                    {isEditing ? "Update & Print" : "Save & Print"}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right: editable preview + toolbar + inspector */}
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="col-lg-7">
                        <div className="card rounded-3 shadow-sm h-100">
                            <div className="card-body">
                                <h5 className="card-title fw-semibold text-dark mb-3">Editable Preview</h5>

                                {/* Quick template controls */}
                                <div className="row g-2 align-items-center mb-2">
                                    <div className="col-12 col-md-auto">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted">Default text color</span>
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={tpl.textColor || DEFAULT_TEXT}
                                                onChange={(e) => setTpl((p) => ({ ...p, textColor: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted">BG opacity</span>
                                            <input
                                                type="range"
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                className="flex-grow-1"
                                                value={tpl.bgOpacity ?? 0.28}
                                                onChange={(e) => setTpl((p) => ({ ...p, bgOpacity: parseFloat(e.target.value) }))}
                                            />
                                            <span className="text-xs text-gray-500">
                                                {(tpl.bgOpacity ?? 0.28).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted">BG blur (px)</span>
                                            <input
                                                type="range"
                                                min={0}
                                                max={12}
                                                step={1}
                                                className="flex-grow-1"
                                                value={tpl.bgBlurAmount ?? 0}
                                                onChange={(e) => setTpl((p) => ({ ...p, bgBlurAmount: parseInt(e.target.value, 10) }))}
                                            />
                                            <span className="text-xs text-gray-500">
                                                {tpl.bgBlurAmount ?? 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Scaled canvas only */}
                                <ScaledPreview baseWidth={800} baseHeight={1200}>
                                    <EditableCanvas
                                        tpl={tpl}
                                        setTpl={setTpl}
                                        selectedId={selectedId}
                                        setSelectedId={setSelectedId}
                                        previewVars={{
                                            receiverName: formData.receiverName || "Receiver",
                                            senderName: formData.senderName || "Sender",
                                            message: "", // message removed from the form
                                        }}
                                    />
                                </ScaledPreview>

                                {/* Add toolbar */}
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    <button type="button" onClick={() => addBlock("custom")} className="btn btn-light btn-sm d-inline-flex align-items-center gap-2">
                                        <Plus size={16} /> Add Text
                                    </button>
                                    <button type="button" onClick={() => addBlock("heading")} className="btn btn-light btn-sm d-inline-flex align-items-center gap-2">
                                        <Plus size={16} /> Add Heading
                                    </button>
                                    <button type="button" onClick={() => addBlock("subheading")} className="btn btn-light btn-sm d-inline-flex align-items-center gap-2">
                                        <Plus size={16} /> Add Subheading
                                    </button>
                                    <button type="button" onClick={() => addBlock("receiver")} className="btn btn-light btn-sm d-inline-flex align-items-center gap-2">
                                        <Plus size={16} /> Add Receiver
                                    </button>
                                    <button type="button" onClick={() => addBlock("sender")} className="btn btn-light btn-sm d-inline-flex align-items-center gap-2">
                                        <Plus size={16} /> Add Sender
                                    </button>

                                    <div className="flex-grow-1" />
                                    <button
                                        type="button"
                                        onClick={() => setTpl((p) => ({ ...p, layout: [] }))}
                                        className="btn btn-light btn-sm d-inline-flex align-items-center gap-2"
                                        title="Clear all blocks"
                                    >
                                        <RotateCcw size={16} /> Clear
                                    </button>
                                </div>

                                {/* Inspector */}
                                <div className="bg-white rounded-xl border p-3 mt-3">
                                    <h4 className="fs-6 fw-semibold mb-2">Inspector</h4>
                                    {!selectedBlock ? (
                                        <p className="small text-muted mb-0">Select a text block in the preview to edit its properties or click inside a block to edit text.</p>
                                    ) : (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <span className="small text-muted">Block type</span>
                                                <span className="px-2 py-1 rounded bg-light small">{selectedBlock.type}</span>
                                            </div>

                                            <label className="form-label small text-muted mb-1">Text</label>
                                            <textarea
                                                className="form-control form-control-sm mb-3"
                                                rows={3}
                                                value={selectedBlock.text}
                                                onChange={(e) => updateBlock(selectedBlock.id, { text: e.target.value })}
                                                placeholder="Use {receiverName}, {senderName}, {message}"
                                            />

                                            <div className="row g-3">
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={selectedBlock.style?.color || tpl.textColor || DEFAULT_TEXT}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { color: e.target.value, gradient: undefined })}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Font size</label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min={8}
                                                        max={200}
                                                        value={selectedBlock.style?.fontSize ?? 28}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { fontSize: parseInt(e.target.value || "0", 10) })}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Weight</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={selectedBlock.style?.fontWeight ?? 700}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { fontWeight: parseInt(e.target.value, 10) })}
                                                    >
                                                        {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                                                            <option key={w} value={w}>{w}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Align</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={selectedBlock.style?.textAlign ?? "left"}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { textAlign: e.target.value })}
                                                    >
                                                        <option value="left">Left</option>
                                                        <option value="center">Center</option>
                                                        <option value="right">Right</option>
                                                    </select>
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Rotate (deg)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={selectedBlock.rotate ?? 0}
                                                        onChange={(e) => updateBlock(selectedBlock.id, { rotate: parseInt(e.target.value || "0", 10) })}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label small text-muted mb-1">Width (%)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min={5}
                                                        max={100}
                                                        value={selectedBlock.w}
                                                        onChange={(e) => updateBlock(selectedBlock.id, { w: clamp(parseFloat(e.target.value || "0"), 5, 100) })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-3 mt-3 ml-3 pl-1">
                                                <label className="form-check-label small d-inline-flex align-items-center gap-2 mr-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!!selectedBlock.style?.italic}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { italic: e.target.checked })}
                                                    />
                                                    Italic
                                                </label>
                                                <label className="form-check-label small d-inline-flex align-items-center gap-2 mr-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!!selectedBlock.style?.uppercase}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { uppercase: e.target.checked })}
                                                    />
                                                    Uppercase
                                                </label>
                                                <label className="form-check-label small d-inline-flex align-items-center gap-2 mr-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!!selectedBlock.style?.shadow}
                                                        onChange={(e) => updateBlockStyle(selectedBlock.id, { shadow: e.target.checked })}
                                                    />
                                                    Shadow
                                                </label>
                                            </div>

                                            <div className="mt-3">
                                                <label className="form-label small text-muted mb-1">Gradient (Tailwind classes)</label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    placeholder="from-pink-500 via-purple-500 to-blue-500"
                                                    value={selectedBlock.style?.gradient || ""}
                                                    onChange={(e) => updateBlockStyle(selectedBlock.id, { gradient: e.target.value || undefined })}
                                                />
                                                <p className="text-muted small mt-1 mb-0">
                                                    Leave empty for solid color. When set, text uses gradient with <code>bg-clip-text text-transparent</code>.
                                                </p>
                                            </div>

                                            <div className="d-flex justify-content-between mt-3">
                                                <button type="button" onClick={removeSelected} className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2">
                                                    <Trash2 size={16} /> Remove block
                                                </button>
                                                <div className="text-muted small">Drag text to move. Use the right dot to resize.</div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <p className="small text-muted mt-3 text-center mb-0">
                                    Click a text to edit. Drag to move; use the right dot to resize; fine-tune in the Inspector.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export default QRForm;
